/**
 * The practitioner's own consult list — GET /api/tenant/consults.
 *
 * The session is the money object of a per-minute consult: it carries the measured
 * minutes, the captured amount and the evidence grade. This hook is the tenant-side read
 * of it, and it is the only one — `TelehealthAdmin` used to list `/telehealth/rooms`,
 * which is the door, not the sale, and is being removed.
 *
 * LIVENESS IS A LOCAL FLAG, NOT A REF. `const alive = useRef(true)` with a cleanup that
 * sets it false is a LATCH: `src/index.js` wraps the app in `React.StrictMode`, so React
 * mounts, cleans up and re-mounts, and nothing ever sets the ref back — every subsequent
 * resolution is discarded and the screen stays on "loading" for ever. That defect shipped
 * once already (`super_admin/__tests__/GatewayConsole.strictMode.test.js` documents it).
 * A `let cancelled = false` declared INSIDE the effect body is fresh per run, so the
 * discarded run is the stale one and the live run always lands.
 *
 * `params` is destructured into primitives on purpose: an object literal from the caller
 * has a new identity every render, and depending on it would restart the fetch in a loop.
 *
 * `gatewayApi` is an optional trailing param defaulting to the module singleton,
 * mirroring the component-level DI rule (contract §9).
 */
import { useCallback, useEffect, useState } from "react";
import defaultGatewayApi from "../api";

export function useTenantConsults({ status, from, to, limit } = {}, gatewayApi = defaultGatewayApi) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bumped by `reload()` to re-run the effect below, so the fetch and its liveness guard
  // live in ONE place: a `reload` that fetched on its own would need a second copy of the
  // guard, and the two copies are how they drift.
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => { setNonce((n) => n + 1); }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    if (from) params.from = from;
    if (to) params.to = to;
    if (limit) params.limit = limit;

    gatewayApi.listTenantConsults(params).then(
      (data) => {
        if (cancelled) return;
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(Number(data?.total ?? 0));
        setError(null);
        setLoading(false);
      },
      (e) => {
        if (cancelled) return;
        // `rows` is deliberately left alone: a failed refresh must not blank a list the
        // practitioner was already reading. `rows === null` still means "never read".
        setError(e);
        setLoading(false);
      },
    );

    return () => { cancelled = true; };
  }, [status, from, to, limit, gatewayApi, nonce]);

  return { rows, total, loading, error, reload };
}

export default useTenantConsults;
